import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, doc, updateDoc, getDoc, setDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import axios from 'axios';
import { 
  Clock, 
  CheckCircle, 
  Lock, 
  Unlock, 
  ShieldCheck,
  ShieldOff
} from 'lucide-react';
import styles from '../CSS/GroupOptions.module.css';
import { getBestDeviceName } from '../../utils/deviceUtils';

// ✅ Servidor actualizado
const SERVER_URL = 'https://truebavirtual.onrender.com';

// =============================================
// HELPER FUNCTIONS - Lógica de Estados
// =============================================

/**
 * Detecta el estado actual del dispositivo para un grupo específico
 */
const detectDeviceState = (groupRestriction, previousRestriction) => {
  if (!groupRestriction || groupRestriction.status === 'allowed') {
    return 'ALLOWED';
  }

  if (groupRestriction.status === 'paused') {
    return previousRestriction?.wasPermanent
      ? 'PAUSED_FROM_PERMANENT'
      : 'PAUSED_FROM_TEMPORAL';
  }

  if (groupRestriction.status === 'blocked') {
    return groupRestriction.expirationTime
      ? 'BLOCKED_TEMPORAL'
      : 'BLOCKED_PERMANENT';
  }

  return 'ALLOWED'; // Default
};

/**
 * Valida si una acción es válida para el estado actual
 */
const isValidAction = (currentState, isRestrictAction, isPermanent) => {
  const action = isRestrictAction
    ? (isPermanent ? 'restrict-permanent' : 'restrict-temporal')
    : (isPermanent ? 'allow-permanent' : 'allow-temporal');

  const validActions = {
    'ALLOWED': ['restrict-permanent', 'restrict-temporal'],
    'BLOCKED_PERMANENT': ['restrict-temporal', 'allow-permanent', 'allow-temporal'],
    'BLOCKED_TEMPORAL': ['restrict-permanent', 'restrict-temporal', 'allow-permanent', 'allow-temporal'],
    'PAUSED_FROM_PERMANENT': ['restrict-permanent', 'restrict-temporal', 'allow-permanent', 'allow-temporal'],
    'PAUSED_FROM_TEMPORAL': ['restrict-permanent', 'restrict-temporal', 'allow-permanent', 'allow-temporal']
  };

  return validActions[currentState]?.includes(action) || false;
};

const GroupOptions = ({
  groupId,
  selectedDeviceUDIDs,
  onReload,
  setMessage,
  colorClass,
  label,
  onClose,
  anchorEl,
  devices = []
}) => {
  const [isPermanentSelected, setIsPermanentSelected] = useState(false);
  const [selectedTimeOption, setSelectedTimeOption] = useState(null);
  const [currentDevicesState, setCurrentDevicesState] = useState({});
  const [loading, setLoading] = useState(false);
  const optionsRef = useRef(null);
  const db = getFirestore();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    if (optionsRef.current && anchorEl) {
      const buttonRect = anchorEl.getBoundingClientRect();
      const menuWidth = optionsRef.current.offsetWidth;
      
      const leftPosition = buttonRect.left + (buttonRect.width / 2) - (menuWidth / 2);
      const adjustedLeft = Math.max(10, Math.min(leftPosition, window.innerWidth - menuWidth - 10));
      
      optionsRef.current.style.position = 'fixed';
      optionsRef.current.style.top = `${buttonRect.bottom + 5}px`;
      optionsRef.current.style.left = `${adjustedLeft}px`;
    }
  }, [anchorEl]);

  const cleanupExpiredRestrictions = async () => {
    try {
      const currentTime = new Date();
      let changesDetected = false;
      
      for (const udid of selectedDeviceUDIDs) {
        const deviceRef = doc(db, 'devices', udid);
        const deviceSnap = await getDoc(deviceRef);
        
        if (deviceSnap.exists()) {
          const deviceData = deviceSnap.data();
          
          if (deviceData.previousRestrictions && 
              deviceData.previousRestrictions[groupId]) {
            
            const restriction = deviceData.previousRestrictions[groupId];
            
            if (restriction.resumeAfter) {
              const resumeTime = restriction.resumeAfter.toDate();
              
              if (currentTime > resumeTime) {
                console.log(`Limpiando restricción expirada para ${deviceData.name || udid}`);
                changesDetected = true;
                
                const remainingTime = restriction.remainingTime;
                
                if (remainingTime > 0) {
                  const newExpirationTime = new Date(currentTime.getTime() + remainingTime);
                  
                  await updateDoc(deviceRef, {
                    [`groupRestrictions.${groupId}.status`]: 'blocked',
                    [`groupRestrictions.${groupId}.expirationTime`]: Timestamp.fromDate(newExpirationTime),
                    [`previousRestrictions.${groupId}`]: null,
                    lastModified: Timestamp.now()
                  });
                  
                  // ✅ RUTA ACTUALIZADA
                  await axios.post(`${SERVER_URL}/api/jamf/groups/add`, {
                    groupId,
                    udids: [udid],
                    expirationTime: newExpirationTime.toISOString()
                  });
                } else {
                  await updateDoc(deviceRef, {
                    [`groupRestrictions.${groupId}.status`]: 'allowed',
                    [`groupRestrictions.${groupId}.expirationTime`]: null,
                    [`previousRestrictions.${groupId}`]: null,
                    lastModified: Timestamp.now()
                  });
                }
              }
            }
          }
        }
      }
      
      if (changesDetected) {
        onReload();
      }
    } catch (error) {
      console.error("Error al limpiar restricciones expiradas:", error);
      setMessage(`Error: ${error.message}`, "error");
    }
  };

  useEffect(() => {
    setLoading(true);
    
    cleanupExpiredRestrictions();
    
    const unsubscribe = selectedDeviceUDIDs.map((udid) =>
      onSnapshot(doc(db, 'devices', udid), (snapshot) => {
        if (snapshot.exists()) {
          const deviceData = snapshot.data();
          
          const groupRestriction = deviceData.groupRestrictions?.[groupId] || {};
          const expirationTime = groupRestriction.expirationTime?.toDate();
          const currentTime = new Date();

          setCurrentDevicesState(prev => ({
            ...prev,
            [udid]: {
              status: groupRestriction.status || 'allowed',
              expirationTime,
              previousRestrictions: deviceData.previousRestrictions || {},
              name: deviceData.name || udid
            }
          }));

          if (groupRestriction.status === 'blocked' && expirationTime && currentTime > expirationTime) {
            handleExpiration(udid);
          }
        }
        
        setLoading(false);
      })
    );

    return () => unsubscribe.forEach((unsub) => unsub());
  }, [selectedDeviceUDIDs, db]);

  const handleExpiration = async (udid) => {
    try {
      const deviceRef = doc(db, 'devices', udid);
      await updateDoc(deviceRef, {
        [`groupRestrictions.${groupId}.status`]: 'allowed',
        [`groupRestrictions.${groupId}.expirationTime`]: null
      });

      // ✅ RUTA ACTUALIZADA
      await axios.post(`${SERVER_URL}/api/jamf/groups/remove`, {
        groupId,
        udids: [udid],
      });

      setMessage(`El dispositivo ${udid} ha expirado y ha sido revertido.`, "info");
      onReload();
    } catch (err) {
      console.error(`Error al revertir el estado del dispositivo ${udid}:`, err);
      setMessage(`Error: ${err.message}`, "error");
    }
  };

  const getAppName = () => {
    return label || (groupId === 266 ? 'YouTube' : groupId === 133 ? 'Safari' : 'Aplicación');
  };

  const handleAddToGroup = async () => {
    if (!validateSelection()) return;
    setLoading(true);
    await handleGroupChange('add');
    setLoading(false);
  };

  const handleRemoveFromGroup = async () => {
    if (!validateSelection()) return;
    setLoading(true);
    await handleGroupChange('remove');
    setLoading(false);
  };

  const validateSelection = () => {
    if (selectedDeviceUDIDs.length === 0) {
      setMessage('Selecciona un dispositivo.', "error");
      return false;
    }
    if (!isPermanentSelected && !selectedTimeOption) {
      setMessage('Debes seleccionar una opción de tiempo o permanente.', "error");
      return false;
    }
    return true;
  };

  const getExpirationTime = (option) => {
    switch (option) {
      case '5m': return 5 * 60 * 1000;
      case '15m': return 1 * 60 * 1000;
      case '30m': return 30 * 60 * 1000;
      case '45m': return 45 * 60 * 1000;
      case '50m': return 50 * 60 * 1000;
      case '1h': return 60 * 60 * 1000;
      case '2h': return 2 * 60 * 60 * 1000;
      case '12h': return 12 * 60 * 60 * 1000;
      case '1d': return 24 * 60 * 60 * 1000;
      case '1 sem': return 7 * 24 * 60 * 60 * 1000;
      case '1 mes': return 30 * 24 * 60 * 60 * 1000;
      case '3 mes': return 90 * 24 * 60 * 60 * 1000;
      default: return 0;
    }
  };

  const formatExpirationTime = (timeMs) => {
    if (timeMs < 60 * 1000) return `${Math.floor(timeMs / 1000)} segundos`;
    if (timeMs < 60 * 60 * 1000) return `${Math.floor(timeMs / (60 * 1000))} minutos`;
    if (timeMs < 24 * 60 * 60 * 1000) return `${Math.floor(timeMs / (60 * 60 * 1000))} horas`;
    if (timeMs < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(timeMs / (24 * 60 * 60 * 1000))} días`;
    if (timeMs < 30 * 24 * 60 * 60 * 1000) return `${Math.floor(timeMs / (7 * 24 * 60 * 60 * 1000))} semanas`;
    return `${Math.floor(timeMs / (30 * 24 * 60 * 60 * 1000))} meses`;
  };

  const handleGroupChange = async (action) => {
    try {
      // Procesar todos los dispositivos en paralelo
      const results = await Promise.allSettled(
        selectedDeviceUDIDs.map(async (udid) => {
          const deviceRef = doc(db, 'devices', udid);
          const deviceSnap = await getDoc(deviceRef);
          const deviceExists = deviceSnap.exists();
          const deviceData = deviceExists ? deviceSnap.data() : null;
        
          let deviceName = getBestDeviceName(
            udid, 
            deviceData, 
            devices.reduce((map, device) => {
              if (device.UDID && device.name) {
                map[device.UDID] = device.name;
              }
              return map;
            }, {})
          );
          
          if (devices && Array.isArray(devices)) {
            const matchingDevice = devices.find(d => d.UDID === udid);
            if (matchingDevice && matchingDevice.name) {
              deviceName = matchingDevice.name;
            }
          }
        
          if (!deviceExists) {
            await setDoc(deviceRef, {
              id: udid,
              status: 'allowed',
              name: deviceName,
              udid: udid,
              createdAt: Timestamp.now(),
              lastModified: Timestamp.now(),
              groupRestrictions: {},
              previousRestrictions: {}
            });
          } else if (deviceData.name !== deviceName) {
            await updateDoc(deviceRef, {
              name: deviceName,
              lastModified: Timestamp.now()
            });
          }
    
          if (action === 'add') {
            const currentTime = new Date();
            let newExpirationTime;
            
            const currentGroupRestriction = deviceData?.groupRestrictions?.[groupId] || {};
            const currentExpirationTime = currentGroupRestriction.expirationTime?.toDate();
            
            const isAlreadyBlocked = currentGroupRestriction.status === 'blocked';
            
            if (isPermanentSelected) {
              newExpirationTime = null;
            } else {
              const newDuration = getExpirationTime(selectedTimeOption);
              const proposedExpirationTime = new Date(currentTime.getTime() + newDuration);
    
              if (isAlreadyBlocked && currentExpirationTime && currentTime < currentExpirationTime) {
                newExpirationTime = proposedExpirationTime;
              } else {
                newExpirationTime = proposedExpirationTime;
              }
            }
    
            await updateDoc(deviceRef, {
              [`groupRestrictions.${groupId}`]: {
                status: 'blocked',
                expirationTime: newExpirationTime ? Timestamp.fromDate(newExpirationTime) : null,
                groupId: groupId,
                lastModified: Timestamp.now()
              },
              [`previousRestrictions.${groupId}`]: null,
              lastModified: Timestamp.now()
            });
    
            return { udid, deviceName, action: 'add', expirationTime: newExpirationTime };
          } else {
            const currentTime = new Date();
            const currentGroupRestriction = deviceData?.groupRestrictions?.[groupId] || {};
            const currentExpirationTime = currentGroupRestriction.expirationTime?.toDate();
            const isBlocked = currentGroupRestriction.status === 'blocked';
            const isPaused = currentGroupRestriction.status === 'paused';
            
            if (isPermanentSelected) {
              await updateDoc(deviceRef, {
                [`groupRestrictions.${groupId}`]: {
                  status: 'allowed',
                  expirationTime: null,
                  groupId: groupId,
                  lastModified: Timestamp.now()
                },
                [`previousRestrictions.${groupId}`]: null,
                lastModified: Timestamp.now()
              });

              return { udid, deviceName, action: 'remove', permanent: true };
            } else if (isBlocked && !currentExpirationTime) {
              // ✅ CASO: Restricción PERMANENTE → Permitir TEMPORAL
              // Guardar que la restricción original era permanente
              const allowDuration = getExpirationTime(selectedTimeOption);
              const resumeAfter = new Date(currentTime.getTime() + allowDuration);

              await updateDoc(deviceRef, {
                [`groupRestrictions.${groupId}`]: {
                  status: 'paused',
                  expirationTime: Timestamp.fromDate(resumeAfter),
                  groupId: groupId,
                  lastModified: Timestamp.now()
                },
                [`previousRestrictions.${groupId}`]: {
                  originalExpirationTime: null,  // ← null indica que era permanente
                  remainingTime: null,  // ← null indica que debe volver a permanente
                  pausedAt: Timestamp.now(),
                  resumeAfter: Timestamp.fromDate(resumeAfter),
                  groupId: groupId,
                  wasPermanent: true  // ← Flag para indicar que era permanente
                },
                lastModified: Timestamp.now()
              });

              setTimeout(async () => {
                try {
                  const updatedDocSnap = await getDoc(deviceRef);
                  if (updatedDocSnap.exists()) {
                    const updatedData = updatedDocSnap.data();
                    const pauseInfo = updatedData.previousRestrictions?.[groupId];

                    if (pauseInfo && pauseInfo.wasPermanent) {
                      // Volver a restricción PERMANENTE
                      await updateDoc(deviceRef, {
                        [`groupRestrictions.${groupId}`]: {
                          status: 'blocked',
                          expirationTime: null,  // ← Permanente de nuevo
                          groupId: groupId,
                          lastModified: Timestamp.now()
                        },
                        [`previousRestrictions.${groupId}`]: null,
                        lastModified: Timestamp.now()
                      });

                      await axios.post(`${SERVER_URL}/api/jamf/groups/add`, {
                        groupId,
                        udids: [udid],
                        expirationTime: null  // ← Permanente
                      });
                    }
                  }
                  onReload();
                } catch (error) {
                  console.error("Error al reanudar la restricción permanente:", error);
                }
              }, allowDuration);

              return { udid, deviceName, action: 'pause', allowDuration, wasPermanent: true };
            } else if (isBlocked && currentExpirationTime && currentTime < currentExpirationTime) {
              const remainingTime = currentExpirationTime.getTime() - currentTime.getTime();
              const allowDuration = getExpirationTime(selectedTimeOption);
              
              const resumeAfter = new Date(currentTime.getTime() + allowDuration);
              
              await updateDoc(deviceRef, {
                [`groupRestrictions.${groupId}`]: {
                  status: 'paused',
                  expirationTime: Timestamp.fromDate(resumeAfter),
                  groupId: groupId,
                  lastModified: Timestamp.now()
                },
                [`previousRestrictions.${groupId}`]: {
                  originalExpirationTime: Timestamp.fromDate(currentExpirationTime),
                  remainingTime: remainingTime,
                  pausedAt: Timestamp.now(),
                  resumeAfter: Timestamp.fromDate(resumeAfter),
                  groupId: groupId
                },
                lastModified: Timestamp.now()
              });
    
              setTimeout(async () => {
                try {
                  const updatedDocSnap = await getDoc(deviceRef);
                  if (updatedDocSnap.exists()) {
                    const updatedData = updatedDocSnap.data();
                    const pauseInfo = updatedData.previousRestrictions?.[groupId];
                    
                    if (pauseInfo) {
                      const nowTime = new Date();
                      const adjustedRemainingTime = pauseInfo.remainingTime;
                      
                      if (adjustedRemainingTime > 0) {
                        const newExpirationTime = new Date(nowTime.getTime() + adjustedRemainingTime);
                        
                        await updateDoc(deviceRef, {
                          [`groupRestrictions.${groupId}`]: {
                            status: 'blocked',
                            expirationTime: Timestamp.fromDate(newExpirationTime),
                            groupId: groupId,
                            lastModified: Timestamp.now()
                          },
                          [`previousRestrictions.${groupId}`]: null,
                          lastModified: Timestamp.now()
                        });
                        
                        await axios.post(`${SERVER_URL}/api/jamf/groups/add`, {
                          groupId,
                          udids: [udid],
                          expirationTime: newExpirationTime.toISOString()
                        });
                      } else {
                        await updateDoc(deviceRef, {
                          [`groupRestrictions.${groupId}`]: {
                            status: 'allowed',
                            expirationTime: null,
                            groupId: groupId,
                            lastModified: Timestamp.now()
                          },
                          [`previousRestrictions.${groupId}`]: null,
                          lastModified: Timestamp.now()
                        });
                      }
                    }
                  }
                  onReload();
                } catch (error) {
                  console.error("Error al reanudar la restricción:", error);
                }
              }, allowDuration);
              
              return { udid, deviceName, action: 'pause', allowDuration };
            } else if (isPaused) {
              const currentPauseInfo = deviceData?.previousRestrictions?.[groupId];
              
              if (currentPauseInfo) {
                const allowDuration = getExpirationTime(selectedTimeOption);
                const newResumeAfter = new Date(currentTime.getTime() + allowDuration);
                
                await updateDoc(deviceRef, {
                  [`groupRestrictions.${groupId}.expirationTime`]: Timestamp.fromDate(newResumeAfter),
                  [`previousRestrictions.${groupId}.resumeAfter`]: Timestamp.fromDate(newResumeAfter),
                  lastModified: Timestamp.now()
                });
                
                return { udid, deviceName, action: 'update_pause', allowDuration };
              }
            } else {
              await updateDoc(deviceRef, {
                [`groupRestrictions.${groupId}`]: {
                  status: 'allowed',
                  expirationTime: null,
                  groupId: groupId,
                  lastModified: Timestamp.now()
                },
                [`previousRestrictions.${groupId}`]: null,
                lastModified: Timestamp.now()
              });
    
              return { udid, deviceName, action: 'remove' };
            }
          }
        })
      );

      // Procesar resultados y hacer llamadas a la API en lote
      const successfulResults = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);

      const failedResults = results.filter(result => result.status === 'rejected');

      // Agrupar por acción para hacer llamadas en lote
      const addActions = successfulResults.filter(r => r.action === 'add');
      const removeActions = successfulResults.filter(r => r.action === 'remove' || r.action === 'pause');

      // Hacer llamadas a la API en lote
      if (addActions.length > 0) {
        const udidsToAdd = addActions.map(r => r.udid);
        const expirationTime = addActions[0].expirationTime;
        
        await axios.post(`${SERVER_URL}/api/jamf/groups/add`, {
          groupId,
          udids: udidsToAdd,
          expirationTime: expirationTime ? expirationTime.toISOString() : null
        });
      }

      if (removeActions.length > 0) {
        const udidsToRemove = removeActions.map(r => r.udid);
        
        await axios.post(`${SERVER_URL}/api/jamf/groups/remove`, {
          groupId,
          udids: udidsToRemove,
        });
      }

      // Mostrar mensaje de resumen
      const successCount = successfulResults.length;
      const failCount = failedResults.length;

      if (successCount > 0) {
        const actionText = action === 'add' ? 'bloqueados' : 'permitidos';
        setMessage(
          `${successCount} dispositivo${successCount > 1 ? 's' : ''} ${actionText} correctamente${
            failCount > 0 ? ` (${failCount} fallaron)` : ''
          }`, 
          failCount > 0 ? "warning" : "success"
        );
      } else if (failCount > 0) {
        setMessage(`Error: No se pudo procesar ningún dispositivo`, "error");
      }

      onReload();
      if (onClose) {
        setTimeout(onClose, 500);
      }
    } catch (err) {
      console.error("Error al cambiar el estado de los dispositivos:", err);
      setMessage(`Error: ${err.message}`, "error");
    }
  };

  const handlePermanentToggle = (event) => {
    event.stopPropagation();
    setIsPermanentSelected((prev) => !prev);
    setSelectedTimeOption(null);
  };

  const handleTimeOptionClick = (option, event) => {
    event.stopPropagation();
    setSelectedTimeOption((prev) => (prev === option ? null : option));
    setIsPermanentSelected(false);
  };

  const getDevicesStateMessage = () => {
    if (!selectedDeviceUDIDs || selectedDeviceUDIDs.length === 0) return null;
    
    const blockedCount = selectedDeviceUDIDs.filter(
      udid => currentDevicesState[udid]?.status === 'blocked'
    ).length;
    
    if (blockedCount === 0) return null;
    
    return (
      <div className={styles.statusBadge}>
        <Lock size={12} />
        <span>{blockedCount}/{selectedDeviceUDIDs.length}</span>
      </div>
    );
  };

  const timeOptions = [
    { value: '15m', label: '15 min' },
    { value: '30m', label: '30 min' },
    { value: '1h', label: '1 hora' },
    { value: '2h', label: '2 horas' },
    { value: '12h', label: '12 horas' },
    { value: '1d', label: '1 día' },
    { value: '1 sem', label: '1 semana' },
  ];

  const handleContainerClick = (e) => {
    e.stopPropagation();
  };

  const areDevicesBlocked = () => {
    if (!selectedDeviceUDIDs || selectedDeviceUDIDs.length === 0) return false;
    
    const blockedCount = selectedDeviceUDIDs.filter(
      udid => currentDevicesState[udid]?.status === 'blocked'
    ).length;
    
    return blockedCount > 0;
  };

  const isBlocked = areDevicesBlocked();
  const appName = getAppName();

  return (
    <div 
      className={`${styles.optionsDropdown} ${styles[colorClass]}`} 
      ref={optionsRef}
      onClick={handleContainerClick}
      style={{ zIndex: 1000 }}
    >
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      ) : (
        <>
          <div className={styles.dropdownHeader}>
            <span>Opciones de {label}</span>
            {getDevicesStateMessage()}
          </div>
          
          <div className={styles.dropdownDivider}></div>
          
          <div className={styles.optionsList}>
            <button 
              className={`${styles.optionItem} ${isPermanentSelected ? styles.selected : ''}`}
              onClick={handlePermanentToggle}
            >
              {isBlocked ? <Unlock size={14} /> : <Lock size={14} />}
              <span>Permanente</span>
              {isPermanentSelected && <CheckCircle size={14} className={styles.checkIcon} />}
            </button>
            
            <div className={styles.dropdownDivider}></div>
            
            <div className={styles.optionsGroup}>
              <div className={styles.optionsGroupLabel}>
                Temporal
              </div>
              {timeOptions.map((option) => (
                <button
                  key={option.value}
                  className={`${styles.optionItem} ${selectedTimeOption === option.value ? styles.selected : ''}`}
                  onClick={(event) => handleTimeOptionClick(option.value, event)}
                >
                  <Clock size={14} />
                  <span>{option.label}</span>
                  {selectedTimeOption === option.value && <CheckCircle size={14} className={styles.checkIcon} />}
                </button>
              ))}
            </div>
            
            <div className={styles.dropdownDivider}></div>
            
            <div className={styles.actionButtons}>
              <button
                className={`${styles.actionButton} ${styles.blockButton} ${isPermanentSelected || selectedTimeOption ? '' : styles.disabled}`}
                onClick={handleAddToGroup}
                disabled={!isPermanentSelected && !selectedTimeOption}
              >
                <ShieldOff size={14} />
                <span>Restringir {appName}</span>
              </button>
              
              <button
                className={`${styles.actionButton} ${styles.allowButton} ${isPermanentSelected || selectedTimeOption ? '' : styles.disabled}`}
                onClick={handleRemoveFromGroup}
                disabled={!isPermanentSelected && !selectedTimeOption}
              >
                <ShieldCheck size={14} />
                <span>Permitir {appName}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GroupOptions;